import { Product, Review } from '@virginexperiencedays/products';
import { slugToName } from '@virginexperiencedays/search/utils/category';
import config from '../../../SEO/structured_data.config.json';

const productData = ({
  product,
  reviews: reviewsList,
  pagePathOverride,
  hideReviews = false,
  hideOffers = false,
}: {
  product: Product;
  reviews: Review[];
  pagePathOverride?: string;
  hideReviews?: boolean;
  hideOffers?: boolean;
}) => {
  const {
    title,
    media,
    categoryHierarchy,
    reviews,
    price: { displayPrice = null, currency = null } = {
      displayPrice: null,
      currency: null,
    },
    seo: { description = null } = {
      title: null,
      description: null,
    },
    sku,
    status,
    stock,
    slug,
  } = product;

  const { organization, website } = config;

  const { lvl0, lvl1, lvl2 } = categoryHierarchy ?? {};
  const { mainImage, images } = media ?? {};
  const { averageRating: ratingValue, count: ratingCount } = reviews ?? {};

  const pageUrl = website?.url?.replace(/\/$/, '');
  const pagePath = pagePathOverride || `/product/${slug}`;

  const image = [mainImage?.url].concat(images?.map((img) => img?.url) || []).filter(Boolean);

  return {
    '@type': 'Product',
    name: title,
    description: description?.length
      ? description
      : `If you're looking for gift ideas then Virgin Experience can offer a wide range of different kinds of gift experiences such as a ${title}`,
    sku,
    category: category(
      // categoryHierarchy?.lvl2 || categoryHierarchy?.lvl1 || categoryHierarchy?.lvl0
      lvl2 || lvl1 || lvl0
    ),
    mainEntityOfPage: {
      '@id': `${pageUrl}${pagePath}#webpage`,
    },
    brand: {
      '@type': 'Brand',
      name: 'Virgin Experience Days',
    },
    aggregateRating:
      ratingValue > 1 && ratingCount
        ? {
            '@type': 'AggregateRating',
            ratingValue: `${ratingValue}`,
            ratingCount: `${ratingCount}`,
            bestRating: '5',
          }
        : null,
    review:
      !hideReviews && ratingValue > 1
        ? reviewsList?.map(({ rating: ratingValue, content, dateCreated, reviewerName }, i) => ({
            '@type': 'Review',
            '@id': `${pageUrl}#review-${i}`,
            description: content,
            datePublished: dateCreated,
            reviewRating: {
              '@type': 'Rating',
              ratingValue: ratingValue?.toString() ?? null,
              bestRating: '5',
              worstRating: '1',
            },
            author: {
              '@type': 'Person',
              name: reviewerName,
            },
          }))
        : null,
    offers:
      !hideOffers && displayPrice
        ? {
            '@type': 'Offer',
            price: `${displayPrice}`,
            priceCurrency: currency,
            availability:
              ((stock && stock > 0) || stock === null) && status !== 'inactive'
                ? 'https://schema.org/InStock'
                : 'https://schema.org/OutOfStock',
            itemCondition: 'NewCondition',
            url: `${pageUrl}${pagePath}`,
            hasMerchantReturnPolicy: {
              '@type': 'MerchantReturnPolicy',
              applicableCountry: 'GB',
              returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
              merchantReturnDays: 30,
              returnFees: 'https://schema.org/FreeReturn',
            },
            seller: {
              '@id': organization['@id'],
            },
          }
        : null,
    image,
  };
};

const category = (crumbs: String) =>
  crumbs
    ?.split('/')
    .map((slug) => slugToName(slug))
    .join(' > ');

export default productData;
